import React, { useState, useEffect, useContext, useRef } from "react";
import { Button, Input, DatePicker } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
	componentSelectedAtom,
	datosFormularioAtom,
	signatureModalVisibleAtom,
	signaturesMetadataAtom,
	urlFirmaHolografaAtom,
	urlHuellaDactilarAtom,
	variablesGlobalesAtom,
	urlFotoAtom,
	componentesAtom,
	visibilidadComponentesAtom,
	loadingAtom,
} from "../../../../VisorFormularioStorage";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import { atomWithObservable } from "jotai/utils";
import { TipoControl } from "enum";
import moment from "moment";
import "moment/locale/es";
import { SecurityContext } from "context/SecurityContextProvider";
import html2canvas from "html2canvas";
import { SignaturePerson } from "components/common/VisorFormulario/constantes";
import produce from "immer";
import { UbigeoService } from "services";

const ControlPdf = ({ componente }) => {
	moment.locale("es");
	const variables = useAtomValue(variablesGlobalesAtom);
	const datosFormulario = useAtomValue(datosFormularioAtom);
	const urlFirmaHolografa = useAtomValue(urlFirmaHolografaAtom);
	const urlHuellaDactilar = useAtomValue(urlHuellaDactilarAtom);
	const urlFoto = useAtomValue(urlFotoAtom);
	const [visibilidadComponentes, setVisibilidadComponentes] = useAtom(visibilidadComponentesAtom);
	const componentes = useAtomValue(componentesAtom); // Asegúrate de obtener todos los componentes
	const loading = useAtomValue(loadingAtom);

	// Crear un mapa de componentes por key/name para búsqueda rápida
	const componentesMap = React.useMemo(() => {
		const map = {};
		if (componentes && Array.isArray(componentes)) {
			componentes.forEach(comp => {
				if (comp.type === TipoControl.RADIO_BUTTON) {
					map[comp.name] = comp;
				} else {
					map[comp.key] = comp;
				}
			});
		}
		return map;
	}, [componentes]);

	const clavesGrupoUnicidad = React.useMemo(() => {
		if (!componente.grupoUnicidad || !componentes || !Array.isArray(componentes)) {
			return [];
		}
		return componentes
			.filter(
				(c) =>
					c.type === TipoControl.CAJA_TEXTO &&
					c.grupoUnicidad === componente.grupoUnicidad &&
					c.key !== componente.key
			)
			.map((c) => c.key);
	}, [componentes, componente.grupoUnicidad, componente.key]);

	const {
		control,
		setValue,
		getValues,
		register,
		watch,
		trigger,
		unregister,
		formState: { errors },
	} = useFormContext();

	const setSignatureModalVisible = useSetAtom(signatureModalVisibleAtom);
	const setComponentSelected = useSetAtom(componentSelectedAtom);

	const [signaturesMetadata, setSignaturesMetadata] = useAtom(signaturesMetadataAtom);

	const [signature, setSignature] = useState(undefined);
	const [imagen, setImagen] = useState(undefined);

	const [visible, setVisible] = useState(componente.condicional ? false : true);

	const { getUsuarioAutenticado } = useContext(SecurityContext);

	const isExplomin = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20501523837" ? true : false;

	const watchControl = useWatch({
		name: componente.type === TipoControl.RADIO_BUTTON ? componente.name : componente.key,
	});

	useEffect(() => {
		const inicializar = () => {
			if (componente.type === TipoControl.RADIO_BUTTON && watchControl !== "") {
				setVisibilidadComponentes({ ...visibilidadComponentes, [componente.name]: watchControl });
			} else if (componente.type === TipoControl.CHECKBOX && watchControl !== "") {
				const valor = watchControl !== "" && watchControl !== undefined ? "checked" : undefined;
				setVisibilidadComponentes({
					...visibilidadComponentes,
					[componente.key]: valor,
				});
			} else if (componente.type === TipoControl.LISTA && watchControl !== "") {
				setVisibilidadComponentes({ ...visibilidadComponentes, [componente.key]: watchControl });
			}
		};

		inicializar();
	}, [watchControl]);

	const valorControlValidacionLongitud = useWatch({
		name: componente.validacionLongitud?.control || componente.key,
	});
	const reglaLongitudActiva = componente.validacionLongitud?.reglas?.find(
		(regla) => regla.valor === valorControlValidacionLongitud
	);
	const esExcepcionValidacionLongitud = componente.validacionLongitud?.excepciones?.includes(
		valorControlValidacionLongitud
	);
	const reglaLongitudEfectiva =
		reglaLongitudActiva ||
		(!esExcepcionValidacionLongitud ? componente.validacionLongitud?.reglaPorDefecto : undefined);

	useEffect(() => {
		const cargarHuellaDactilar = () => {
			if (
				urlHuellaDactilar &&
				componente.type === TipoControl.IMAGEN &&
				componente.defaultValue === "HUELLA_DACTILAR"
			) {
				fetch(urlHuellaDactilar)
					.then((response) => response.blob())
					.then((blob) => {
						const reader = new FileReader();
						reader.readAsDataURL(blob);
						reader.onload = () => {
							setValue(nombreControl, reader?.result);
							setImagen({
								key: componente.key,
								base64: reader.result,
							});
						};
					})
					.catch((error) => {
						console.log("no se pudo cargar la huella dactilar", error);
					});
			}
		};
		cargarHuellaDactilar();
	}, [urlHuellaDactilar]);

	useEffect(() => {
		const cargarFoto = () => {
			if (urlFoto && componente.type === TipoControl.IMAGEN && componente.defaultValue === "FOTO") {
				fetch(urlFoto)
					.then((response) => response.blob())
					.then((blob) => {
						const reader = new FileReader();
						reader.readAsDataURL(blob);
						reader.onload = () => {
							setValue(nombreControl, reader?.result);
							setImagen({
								key: componente.key,
								base64: reader.result,
							});
						};
					})
					.catch((error) => {
						console.log("no se pudo cargar la foto", error);
					});
			}
		};
		cargarFoto();
	}, [urlFoto]);

	useEffect(() => {
		const cargarFirmaHolografa = () => {
			if (
				urlFirmaHolografa &&
				componente.type === TipoControl.SIGNATURE
				//&&componente.signaturePerson === SignaturePerson.COLABORADOR
			) {
				fetch(urlFirmaHolografa)
					.then((response) => response.blob())
					.then((blob) => {
						const reader = new FileReader();
						reader.readAsDataURL(blob);
						reader.onload = () => {
							setSignaturesMetadata(
								produce((draft) => {
									const signature = draft.find((s) => s.key === componente.key);
									if (signature) {
										signature.base64 = reader.result;
									} else {
										draft.push({
											key: componente.key,
											base64: reader.result,
										});
									}
								})
							);
						};
					})
					.catch((error) => {
						console.log("no se pudo cargar la url de firma hológrafa", error);
					});
			}
		};

		cargarFirmaHolografa();
	}, [urlFirmaHolografa]);

	const getNombreControl = () => {
		let nombreControl = componente.key;
		if (componente.groupBy) {
			nombreControl = `${componente.groupBy.table}.${componente.groupBy.rowNumber - 1}.${componente.groupBy.column
				}`;
		} else {
			if (componente.type === TipoControl.RADIO_BUTTON) {
				nombreControl = componente.name;
			} else if (componente.type === TipoControl.SIGNATURE) {
				nombreControl = componente.key + "_signature_base64";
			} else if (componente.type === TipoControl.IMAGEN) {
				nombreControl = componente.key + "_imagen_base64";
			} else {
				nombreControl = componente.key;
			}
		}
		return nombreControl;
	};

	const getClassName = (nombreControl) => {
		let className = "";
		if (errors) {
			if (componente.type === TipoControl.CAJA_TEXTO) {
				if (componente.groupBy) {
					className = "formulario-input";
					const errores = errors[componente.groupBy.table];
					if (
						errores &&
						errores[componente.groupBy.rowNumber - 1] &&
						errores[componente.groupBy.rowNumber - 1][componente.groupBy.column]
					) {
						className = "formulario-input-error";
					}
				} else {
					className = errors[nombreControl] ? "formulario-input-error" : "formulario-input";
				}
			} else if (componente.type === TipoControl.LISTA) {
				if (componente.groupBy) {
					className = "formulario-list";
					const errores = errors[componente.groupBy.table];
					if (
						errores &&
						errores[componente.groupBy.rowNumber - 1] &&
						errores[componente.groupBy.rowNumber - 1][componente.groupBy.column]
					) {
						className = "formulario-list-error";
					}
				} else {
					className = errors[nombreControl] ? "formulario-list-error" : "formulario-list";
				}
			} else if (componente.type === TipoControl.CHECKBOX) {
				if (componente.groupBy) {
					className = "formulario-control-container";
					const errores = errors[componente.groupBy.table];
					if (
						errores &&
						errores[componente.groupBy.rowNumber - 1] &&
						errores[componente.groupBy.rowNumber - 1][componente.groupBy.column]
					) {
						className = "formulario-control-container-error";
					}
				} else {
					className = errors[nombreControl]
						? "formulario-control-container-error"
						: "formulario-control-container";
				}
			} else if (componente.type === TipoControl.RADIO_BUTTON) {
				if (componente.groupBy) {
					className = "formulario-control-container";
					const errores = errors[componente.groupBy.table];
					if (
						errores &&
						errores[componente.groupBy.rowNumber - 1] &&
						errores[componente.groupBy.rowNumber - 1][componente.groupBy.column]
					) {
						className = "formulario-control-container-error";
					}
				} else {
					className = errors[nombreControl]
						? "formulario-control-container-error"
						: "formulario-control-container";
				}
			} else if (componente.type === TipoControl.SIGNATURE) {
				className = errors[nombreControl] ? "formulario-signature-button-error" : "formulario-signature-button";
			} else if (componente.type === TipoControl.IMAGEN) {
				className = errors[nombreControl] ? "formulario-file-error" : "formulario-file";
			} else if (componente.type === TipoControl.FECHA) {
				className = errors[nombreControl] ? "formulario-input-error" : "formulario-input";
			}
		}
		return className;
	};

	let nombreControl = getNombreControl();
	let className = getClassName(nombreControl);

	const oculto = React.useMemo(
		() => !esComponenteVisible(componente, visibilidadComponentes, componentesMap),
		[componente, visibilidadComponentes, componentesMap]
	);

	useEffect(() => {
		if (oculto && componente.type !== TipoControl.SIGNATURE) {
			unregister(nombreControl);
		}
	}, [oculto]);

	const valorControlPadreUbigeo = useWatch({
		name: componente.fuenteDatosLista?.control || componente.key,
	});
	const [opcionesUbigeo, setOpcionesUbigeo] = useState([]);
	const valorControlPadreUbigeoRef = useRef(valorControlPadreUbigeo);

	useEffect(() => {
		const cargarOpcionesUbigeo = async () => {
			const tipo = componente.fuenteDatosLista?.tipo;
			if (!tipo) {
				return;
			}
			try {
				if (tipo === "DEPARTAMENTO") {
					const departamentos = await UbigeoService.listarDepartamentos();
					setOpcionesUbigeo((departamentos || []).map((d) => ({ key: d.nombre, value: d.nombre })));
				} else if (tipo === "PROVINCIA") {
					if (!valorControlPadreUbigeo) {
						setOpcionesUbigeo([]);
						return;
					}
					const formData = new FormData();
					formData.append("departamento", valorControlPadreUbigeo);
					const provincias = await UbigeoService.listarProvincias(formData);
					setOpcionesUbigeo((provincias || []).map((p) => ({ key: p.provincia, value: p.provincia })));
				} else if (tipo === "DISTRITO") {
					if (!valorControlPadreUbigeo) {
						setOpcionesUbigeo([]);
						return;
					}
					const formData = new FormData();
					formData.append("provincia", valorControlPadreUbigeo);
					const distritos = await UbigeoService.listarDistritos(formData);
					setOpcionesUbigeo(
						(distritos || [])
							.map((d) => ({ key: d.distrito, value: d.distrito }))
							.sort((a, b) => a.value.localeCompare(b.value, "es"))
					);
				}
			} catch (error) {
				console.log("no se pudo cargar las opciones de ubigeo", error);
			}
		};
		cargarOpcionesUbigeo();
	}, [componente.fuenteDatosLista?.tipo, valorControlPadreUbigeo]);

	useEffect(() => {
		if (
			componente.fuenteDatosLista?.control &&
			valorControlPadreUbigeoRef.current !== valorControlPadreUbigeo
		) {
			setValue(nombreControl, "");
		}
		valorControlPadreUbigeoRef.current = valorControlPadreUbigeo;
	}, [valorControlPadreUbigeo]);

	useEffect(() => {
		const loadSignature = async () => {
			if (componente.type === TipoControl.SIGNATURE) {
				const signature = signaturesMetadata.find((s) => s.key === componente.key);
				setSignature(signature);
				setValue(nombreControl, signature?.base64);
			}
		};

		loadSignature();
	}, [signaturesMetadata]);

	useEffect(() => {
		const loadSignature = async () => {
			if (componente.type === TipoControl.SIGNATURE) {
				if (componente.signatureType === "IMAGEN_Y_DESCRIPCION") {
					const divElement = document.getElementById("signature" + nombreControl);

					const scale = 2;
					const canvas = await html2canvas(divElement, { scale });

					const base64Image = canvas.toDataURL("image/png");

					if (base64Image !== "data:,") {
						setValue(nombreControl, base64Image);
					}
				}
			}
		};

		loadSignature();
	}, [signature]);

	const getMensajeRequerido = () => {
		if (componente.required && !componente.correlativo) {
			if (componente.groupBy) {
				return "El campo " + componente.label + " de la tabla " + componente.groupBy.table + " es requerido.";
			}
			return "El campo " + componente.label + " es requerido.";
		}
		return false;
	};

	const reglas = {
		required: getMensajeRequerido(),
	};

	// La regla depende del valor ACTUAL de otro control (ej. banco elegido en listaNombreBanco).
	// React Hook Form no re-registra minLength/maxLength declarativos cuando ese valor cambia
	// despues del primer render, por eso se evalua dentro de un validate() que lee getValues()
	// en el momento de validar (siempre al dia, nunca "congelado" en el valor inicial).
	if (componente.type === TipoControl.CAJA_TEXTO && componente.validacionLongitud) {
		reglas.validate = (value) => {
			const nombreControlLongitud = componente.validacionLongitud.control || componente.key;
			const valorControlLongitud = getValues(nombreControlLongitud);
			const reglaActiva = componente.validacionLongitud.reglas?.find(
				(regla) => regla.valor === valorControlLongitud
			);
			const esExcepcion = componente.validacionLongitud.excepciones?.includes(valorControlLongitud);
			const reglaEfectiva =
				reglaActiva || (!esExcepcion ? componente.validacionLongitud.reglaPorDefecto : undefined);
			if (!reglaEfectiva) {
				return true;
			}
			if (reglaEfectiva.requerido && !value) {
				return componente.groupBy
					? `El campo ${componente.label} de la tabla ${componente.groupBy.table} es requerido.`
					: `El campo ${componente.label} es requerido.`;
			}
			if (value && (value.length < reglaEfectiva.min || value.length > reglaEfectiva.max)) {
				return reglaEfectiva.min === reglaEfectiva.max
					? `El campo ${componente.label} debe tener ${reglaEfectiva.min} dígitos.`
					: `El campo ${componente.label} debe tener entre ${reglaEfectiva.min} y ${reglaEfectiva.max} dígitos.`;
			}
			return true;
		};
	}

	if (componente.type === TipoControl.CAJA_TEXTO && !reglaLongitudActiva) {
		if (componente.minLength) {
			reglas.minLength = {
				value: componente.minLength,
				message: `El campo ${componente.label} debe tener al menos ${componente.minLength} caracteres.`,
			};
		}
		if (componente.maxLength) {
			reglas.maxLength = {
				value: componente.maxLength,
				message: `El campo ${componente.label} debe tener como máximo ${componente.maxLength} caracteres.`,
			};
		}
	}

	if (componente.type === TipoControl.CAJA_TEXTO && componente.pattern) {
		reglas.pattern = {
			value: new RegExp(componente.pattern),
			message: componente.patternMessage || `El campo ${componente.label} tiene un formato inválido.`,
		};
	}

	if (componente.type === TipoControl.CAJA_TEXTO && clavesGrupoUnicidad.length > 0) {
		reglas.validate = (value) => {
			if (!value) {
				return true;
			}
			const formValues = getValues();
			const repetido = clavesGrupoUnicidad.some((clave) => formValues[clave] && formValues[clave] === value);
			return !repetido || `El campo ${componente.label} no puede repetirse con otro ya registrado en la ficha.`;
		};
	}

	var valorInicial = "";
	var fontSize = componente.size && componente.size > 0 ? componente.size + "px" : "12px";

	if (componente.type === TipoControl.CAJA_TEXTO) {
		valorInicial = "";
		if (componente.defaultValue) {
			if (componente.defaultValue === "${PERSONALIZADO}") {
				valorInicial = reemplazarVariables(componente.customDefaultValue, variables);
			} else if (componente.formatDefaultValue) {
				const fm = componente.formatDefaultValue.match(/^\$\{FECHA_ACTUAL([+-])(\d+)\}$/);
				if (fm) {
					const dias = parseInt(fm[2]);
					valorInicial = (fm[1] === '+' ? moment().add(dias, 'days') : moment().subtract(dias, 'days')).format('DD/MM/YYYY');
				} else {
					valorInicial = moment(variables[componente.defaultValue], "DD/MM/YYYY").format(
						componente.formatDefaultValue
					);
				}
			} else {
				valorInicial = variables[componente.defaultValue];
			}
		} else if (datosFormulario) {
			valorInicial = datosFormulario[componente.key];
		}
		if (oculto) {
			return null;
		}

		return (
			<Controller
				name={nombreControl}
				control={control}
				rules={reglas}
				defaultValue={valorInicial}
				render={({ field }) => (
					<Input
						className={className}
						style={{
							top: componente.overlay.top,
							left: componente.overlay.left,
							height: componente.overlay.height,
							width: componente.overlay.width,
							fontSize: fontSize,
						}}
						readOnly={componente.readOnly || componente.correlativo}
						placeholder={componente.correlativo ? "Se asigna al firmar" : undefined}
						maxLength={
							reglaLongitudEfectiva?.max
								? reglaLongitudEfectiva.max
								: componente.maxLength && componente.maxLength > 0
								? componente.maxLength
								: undefined
						}
						autoComplete="off"
						{...field}
						onChange={(e) => {
							field.onChange(e);
							if (clavesGrupoUnicidad.length > 0) {
								trigger(clavesGrupoUnicidad);
							}
						}}
					/>
				)}
			/>
		);
	} else if (componente.type === TipoControl.LISTA) {
		valorInicial = "";
		if (componente.defaultValue) {
			valorInicial = variables[componente.defaultValue];
		} else if (datosFormulario) {
			valorInicial = datosFormulario[componente.key];
		}

		if (oculto) {
			return "";
		}

		return (
			<Controller
				name={nombreControl}
				control={control}
				rules={reglas}
				defaultValue={valorInicial}
				render={({ field }) => (
					<select
						name="select"
						className={className}
						style={{
							top: componente.overlay.top,
							left: componente.overlay.left,
							height: componente.overlay.height,
							width: componente.overlay.width,
							fontSize: fontSize
							// fontSize: componente.overlay.height <= 12 ? "9px" : "12px",
						}}
						{...field}
					>
						<option value=""></option>
						{(componente.fuenteDatosLista?.tipo ? opcionesUbigeo : componente.items || []).map((item) => {
							return (
								<option key={item.key} value={item.key}>
									{item.value}
								</option>
							);
						})}
					</select>
				)}
			/>
		);
	} else if (componente.type === TipoControl.CHECKBOX) {
		console.log("CHECKBOX");
		valorInicial = false;
		if (componente.defaultValue) {
			valorInicial = componente.defaultValue === "checked" ? true : false;
		} else if (datosFormulario) {
			valorInicial = datosFormulario[componente.key] ? true : false;
		}

		if (oculto) {
			return "";
		}

		return (
			<Controller
				key={componente.key}
				name={nombreControl}
				control={control}
				rules={reglas}
				defaultValue={valorInicial}
				render={({ field }) => (
					<div
						className={className}
						style={{
							top: componente.overlay.top,
							left: componente.overlay.left,
							height: componente.overlay.height,
							width: componente.overlay.width,
						}}
					>
						<input
							className="formulario-checkbox"
							{...field}
							type="checkbox"
							onChange={(e) => field.onChange(e.target.checked ? componente.value : false)}
							// defaultChecked={field.value}
							checked={field.value}
						/>
					</div>
				)}
			/>
		);
	} else if (componente.type === TipoControl.RADIO_BUTTON) {
		//valorInicial = false;
		valorInicial = "";
		if (componente.defaultValue) {
			valorInicial = variables[componente.defaultValue]?.toUpperCase();
		} else if (datosFormulario) {
			valorInicial = datosFormulario[componente.name]?.toUpperCase();
		}

		if (oculto) {
			return "";
		}

		return (
			<Controller
				key={componente.key}
				name={nombreControl}
				control={control}
				rules={reglas}
				defaultValue={valorInicial}
				render={({ field }) => {
					return (
						<div
							className={className}
							style={{
								top: componente.overlay.top,
								left: componente.overlay.left,
								height: componente.overlay.height,
								width: componente.overlay.width,
							}}
						>
							<input
								className="formulario-radio-button"
								{...field}
								type="radio"
								value={componente.value.toUpperCase()}
								defaultChecked={valorInicial === componente?.value.toUpperCase()}
							/>
						</div>
					);
				}}
			/>
		);
	} else if (componente.type === TipoControl.SIGNATURE) {
		const onClickBtnFirmar = () => {
			setComponentSelected(componente);
			if (isExplomin) {
				setSignatureModalVisible(false);
			} else {
				setSignatureModalVisible(true);
			}
		};
		return (
			<React.Fragment>
				<input {...register(nombreControl, reglas)} type="hidden" className="formulario-hidden" />
				<div
					id={"signature" + nombreControl}
					className="formulario-signature-container"
					style={{
						top: componente.overlay.top,
						left: componente.overlay.left,
						height: componente.overlay.height,
						width: componente.overlay.width,
						display: signature ? "flex" : "none",
						maxWidth: componente.overlay.width,
						maxHeight: componente.overlay.height,
					}}
					onClick={onClickBtnFirmar}
				>
					<img
						style={{
							flex: "1",
							width: componente.signaturePerson === SignaturePerson.COLABORADOR ? "100%" : "50%",
							width: "50%",
						}}
						src={signature?.base64}
					/>
					{componente.signaturePerson !== SignaturePerson.COLABORADOR && (
						<div
							style={{
								flex: "1",
								fontSize: "0.5em",
								display: "flex",
								justifyContent: "center",
								flexDirection: "column",
							}}
						>
							Firmado por: {getUsuarioAutenticado().nombreCompleto}
							<br />
							Fecha: {moment().format("DD/MM/YYYY")}
						</div>
					)}
				</div>
				<Button
					className={className}
					onClick={onClickBtnFirmar}
					style={{
						top: componente.overlay.top,
						left: componente.overlay.left,
						height: componente.overlay.height,
						width: componente.overlay.width,
						display: signature?.base64 ? "none" : "inherit",
					}}
				>
					<span style={{ fontWeight: "bold", fontSize: "12px" }}>Firmar aquí</span>
				</Button>
			</React.Fragment>
		);
	} else if (componente.type === TipoControl.IMAGEN) {
		const onChangeImagen = (e) => {
			if (e.target.files[0] !== undefined) {
				let reader = new FileReader();
				reader.readAsDataURL(e.target.files[0]);
				reader.onload = () => {
					setValue(nombreControl, reader?.result);
					setImagen({
						key: componente.key,
						base64: reader.result,
					});
				};
			}
		};

		if (oculto) {
			return "";
		}

		return (
			<React.Fragment>
				<input {...register(nombreControl, reglas)} type="hidden" className="formulario-hidden" />
				<label
					className={className}
					style={{
						top: componente.overlay.top,
						left: componente.overlay.left,
						height: componente.overlay.height,
						width: componente.overlay.width,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
					}}
				>
					{imagen?.base64 && (
						<img
							style={{
								top: componente.overlay.top,
								left: componente.overlay.left,
								height: componente.overlay.height,
								width: componente.overlay.width,
								display: imagen ? "flex" : "none",
							}}
							src={imagen?.base64}
						/>
					)}
					{!imagen?.base64 && (
						<React.Fragment>
							<UploadOutlined style={{ fontSize: "16px" }} />
							<span style={{ fontSize: "10px" }}>Subir imagen</span>
						</React.Fragment>
					)}
					{!isExplomin && (
						<input style={{ display: "none" }} type="file" onChange={onChangeImagen} />
					)}
				</label>
			</React.Fragment>
		);
	} else if (componente.type === TipoControl.ETIQUETA) {
		valorInicial = "";
		if (componente.defaultValue) {
			if (componente.defaultValue === "${PERSONALIZADO}") {
				valorInicial = reemplazarVariables(componente.customDefaultValue, variables);
			} else if (componente.formatDefaultValue) {
				const fm = componente.formatDefaultValue.match(/^\$\{FECHA_ACTUAL([+-])(\d+)\}$/);
				if (fm) {
					const dias = parseInt(fm[2]);
					valorInicial = (fm[1] === '+' ? moment().add(dias, 'days') : moment().subtract(dias, 'days')).format('DD/MM/YYYY');
				} else {
					valorInicial = moment(variables[componente.defaultValue], "DD/MM/YYYY").format(
						componente.formatDefaultValue
					);
				}
			} else {
				valorInicial = variables[componente.defaultValue];
			}
		} else if (datosFormulario) {
			valorInicial = datosFormulario[componente.key];
		}

		if (oculto) {
			return "";
		}

		return (
			<Controller
				name={nombreControl}
				control={control}
				rules={reglas}
				defaultValue={valorInicial}
				render={({ field }) => (
					<Input.TextArea
						className="formulario-label"
						style={{
							top: componente.overlay.top,
							left: componente.overlay.left,
							height: componente.overlay.height,
							width: componente.overlay.width,
							fontSize: fontSize,
							resize: 'none',
						}}
						readOnly={true}
						bordered={false}
						{...field}
					/>
				)}
			/>
		);
	} else if (componente.type === TipoControl.FECHA) {
		valorInicial = "";
		if (componente.defaultValue) {
			if (componente.defaultValue === "${PERSONALIZADO}") {
				valorInicial = reemplazarVariables(componente.customDefaultValue, variables);
			} else if (componente.formatDefaultValue) {
				const fm = componente.formatDefaultValue.match(/^\$\{FECHA_ACTUAL([+-])(\d+)\}$/);
				if (fm) {
					const dias = parseInt(fm[2]);
					valorInicial = (fm[1] === '+' ? moment().add(dias, 'days') : moment().subtract(dias, 'days')).format('DD/MM/YYYY');
				} else {
					valorInicial = moment(variables[componente.defaultValue], "DD/MM/YYYY").format(
						componente.formatDefaultValue
					);
				}
			} else {
				valorInicial = variables[componente.defaultValue];
			}
		} else if (datosFormulario) {
			valorInicial = datosFormulario[componente.key];
		}

		if (oculto) {
			return null;
		}

		const disabledDate = (fecha) => {
			if (!componente.restringirFechaActual) return false;
			return !fecha.isSame(moment(), "day");
		};

		return (
			<Controller
				name={nombreControl}
				control={control}
				rules={reglas}
				defaultValue={valorInicial}
				render={({ field }) => (
					<DatePicker
						className={className}
						style={{
							top: componente.overlay.top,
							left: componente.overlay.left,
							height: componente.overlay.height,
							width: componente.overlay.width,
							fontSize: fontSize,
						}}
						format="DD/MM/YYYY"
						placeholder="dd/mm/aaaa"
						disabled={componente.readOnly}
						disabledDate={disabledDate}
						allowClear={!componente.required}
						value={field.value ? moment(field.value, "DD/MM/YYYY") : null}
						onChange={(fecha, fechaTexto) => field.onChange(fechaTexto)}
					/>
				)}
			/>
		);
	}
	return "";
};

function reemplazarVariables(texto, variables) {
	return texto.replace(/\$\{([^}]+)\}/g, (match, key) => {
		const fechaMatch = key.match(/^FECHA_ACTUAL([+-])(\d+)$/);
		if (fechaMatch) {
			const dias = parseInt(fechaMatch[2]);
			const fecha = fechaMatch[1] === '+'
				? moment().add(dias, 'days')
				: moment().subtract(dias, 'days');
			return fecha.format('DD/MM/YYYY');
		}
		if (variables.hasOwnProperty(match)) {
			return variables[match];
		}
		return match;
	});
}

// Función auxiliar para verificar la visibilidad en cascada.
// Soporta "accion": "show" (mostrarse solo si se cumple la condición) y "hidden" (ocultarse si se cumple),
// y sube recursivamente por la cadena de controles padre: si el control del que depende este componente
// está a su vez oculto, este componente también debe considerarse oculto, sin importar el último valor
// que haya quedado registrado para ese control padre.
const esComponenteVisible = (componente, visibilidadComponentes, componentesMap, visitados = new Set()) => {
	const controlPadre = componente.condicional?.control;

	// Si no tiene condicional, o ya se visitó (protección ante configuraciones cíclicas), siempre es visible
	if (!controlPadre || visitados.has(componente.key)) {
		return true;
	}
	visitados.add(componente.key);

	// Compatibilidad con formularios antiguos guardados antes de que existiera "accion": se comportaban como "show".
	const accion = componente.condicional?.accion || "show";
	const valorActual = visibilidadComponentes[controlPadre];

	// Verificar si el valor coincide (soporte para múltiples valores separados por \n)
	const cumpleCondicion =
		valorActual !== undefined &&
		componente.condicional.valor
			.toLowerCase()
			.split("\n")
			.some(valor => valor === valorActual.toLowerCase());

	const visiblePorCondicionPropia = accion === "hidden" ? !cumpleCondicion : cumpleCondicion;

	if (!visiblePorCondicionPropia) {
		return false;
	}

	// CLAVE: Verificar recursivamente si el control padre también está visible
	const componentePadre = componentesMap[controlPadre];
	if (componentePadre) {
		return esComponenteVisible(componentePadre, visibilidadComponentes, componentesMap, visitados);
	}

	return true;
};

export default ControlPdf;
